/*
 * Reusable hero recipe for `<RedoElevatedOnboarding rightPanel={...}>`:
 * background image + dark gradient overlay + bottom-anchored content slot.
 * Originally extracted from intake-modal.tsx (authored by Tony Ramirez).
 */
import classNames from "classnames";
import { memo, ReactNode } from "react";
import * as css from "./redo-elevated-onboarding.module.css";

export interface RedoElevatedOnboardingHeroPanelProps {
  imageUrl: string;
  imageAlt?: string;
  /**
   * Absolute-positioned layer rendered between the gradient and the
   * bottom-anchored content slot. Use for hero-spanning overlays (e.g.
   * a product-preview card that floats over the image).
   */
  overlay?: ReactNode;
  /** Bottom-anchored content rendered above the gradient (e.g. testimonial, logo strip). */
  children?: ReactNode;
  /** Extra className applied to the `.heroContent` wrapper around `children`. */
  contentClassName?: string;
}

export const RedoElevatedOnboardingHeroPanel = memo(
  function RedoElevatedOnboardingHeroPanel({
    imageUrl,
    imageAlt = "",
    overlay,
    children,
    contentClassName,
  }: RedoElevatedOnboardingHeroPanelProps) {
    return (
      <>
        <img
          alt={imageAlt}
          aria-hidden={imageAlt === "" ? true : undefined}
          className={css.image}
          data-arbiter="RedoElevatedOnboardingHeroPanel"
          src={imageUrl}
        />
        <div className={css.heroOverlay} />
        {overlay}
        {children && (
          <div className={classNames(css.heroContent, contentClassName)}>
            {children}
          </div>
        )}
      </>
    );
  },
);
